/**
 * MT WOMO Blog Component
 * Handles mobile filter drawer functionality and client-side pagination
 */

class MTWOMOBlog {
  constructor() {
    this.filterBtn = document.querySelector('.mt-womo-blog__filter-btn');
    this.filterDrawer = document.querySelector('.mt-womo-blog__filter-drawer');
    this.filterOverlay = document.querySelector('.mt-womo-blog__filter-overlay');
    this.filterClose = document.querySelector('.mt-womo-blog__filter-close');
    this.body = document.body;
    this.currentPage = 1;
    this.perPage = 12;
    this.allArticles = [];

    this.init();
  }

  init() {
    // Collect all articles for client-side pagination
    this.collectArticles();
    
    // Get current page from URL
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');
    if (pageParam && parseInt(pageParam) > 0) {
      this.currentPage = parseInt(pageParam);
    }
    
    // Show correct page
    this.showCurrentPage();
    
    // Setup pagination click handlers
    this.setupPaginationHandlers();

    if (!this.filterBtn || !this.filterDrawer) return;

    // Open filter drawer
    this.filterBtn.addEventListener('click', () => {
      this.openDrawer();
    });

    // Close filter drawer
    if (this.filterClose) {
      this.filterClose.addEventListener('click', () => {
        this.closeDrawer();
      });
    }

    // Close drawer when clicking on overlay
    if (this.filterOverlay) {
      this.filterOverlay.addEventListener('click', () => {
        this.closeDrawer();
      });
    }

    // Close drawer when clicking outside
    this.filterDrawer.addEventListener('click', (e) => {
      if (e.target === this.filterDrawer) {
        this.closeDrawer();
      }
    });

    // Handle filter selection in drawer
    this.setupFilterSync();

    // Handle search functionality
    this.setupSearch();

    // Scroll to blog section on page load if there's a page parameter
    this.handlePageLoad();

    // Setup sticky offsets for header and year labels
    this.updateStickyOffsets();
    window.addEventListener('resize', () => this.updateStickyOffsets());
  }

  openDrawer() {
    this.filterDrawer.classList.add('active');
    if (this.filterOverlay) {
      this.filterOverlay.classList.add('active');
    }
    this.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    this.filterDrawer.classList.remove('active');
    if (this.filterOverlay) {
      this.filterOverlay.classList.remove('active');
    }
    this.body.style.overflow = '';
  }
  
  collectArticles() {
    // Get all article cards
    const cards = document.querySelectorAll('.mt-womo-blog__card');
    this.allArticles = Array.from(cards);
  }
  
  showCurrentPage() {
    const startIndex = (this.currentPage - 1) * this.perPage;
    const endIndex = startIndex + this.perPage;
    
    console.log(`Showing page ${this.currentPage}: articles ${startIndex + 1} to ${endIndex} of ${this.allArticles.length}`);
    
    // First, hide ALL article cards in the DOM
    const allCardsInDOM = document.querySelectorAll('.mt-womo-blog__card');
    allCardsInDOM.forEach(card => {
      card.style.display = 'none';
    });
    
    // Then, show only the articles for current page from filtered results
    this.allArticles.forEach((article, index) => {
      if (index >= startIndex && index < endIndex) {
        article.style.display = 'block';
      }
    });
    
    // Hide empty year sections
    this.updateYearSections();
    
    // Update pagination buttons
    this.updatePaginationButtons();
  }
  
  setupPaginationHandlers() {
    // Handle pagination button clicks - use event delegation
    document.addEventListener('click', (e) => {
      // Check if clicked element is a pagination button (link or span)
      const btn = e.target.closest('.mt-womo-blog__pagination-btn');
      if (btn && btn.closest('.mt-womo-blog__pagination')) {
        e.preventDefault();
        e.stopPropagation();
        
        console.log('Pagination button clicked:', btn);
        
        let targetPage = 1;
        
        // If it's a link, get page from href
        if (btn.tagName === 'A') {
          const href = btn.getAttribute('href');
          const urlParams = new URLSearchParams(href.split('?')[1] || '');
          const page = urlParams.get('page');
          targetPage = page ? parseInt(page) : 1;
        } 
        // If it's a span (current page shown as span in Liquid), get from text
        else if (btn.tagName === 'SPAN') {
          const pageNum = parseInt(btn.textContent.trim());
          if (!isNaN(pageNum)) {
            targetPage = pageNum;
          }
        }
        
        // Skip if clicking current page
        if (targetPage === this.currentPage) {
          console.log('Already on page:', targetPage);
          return;
        }
        
        this.currentPage = targetPage;
        console.log('Navigating to page:', this.currentPage);
        
        // Update URL without page reload
        const newUrl = this.currentPage > 1 ? 
          `${window.location.pathname}?page=${this.currentPage}` : 
          window.location.pathname;
        window.history.pushState({ page: this.currentPage }, '', newUrl);
        
        // Show new page
        this.showCurrentPage();
        
        // Scroll to blog section top
        this.scrollToBlogSection();
      }
    }, true); // Use capture phase to ensure we catch the event
    
    // Handle browser back/forward buttons
    window.addEventListener('popstate', (e) => {
      const urlParams = new URLSearchParams(window.location.search);
      const page = urlParams.get('page');
      this.currentPage = page ? parseInt(page) : 1;
      console.log('Popstate - navigating to page:', this.currentPage);
      this.showCurrentPage();
      this.scrollToBlogSection();
    });
  }
  
  scrollToBlogSection() {
    // Scroll to the top of the blog section
    const blogSection = document.querySelector('.mt-womo-blog');
    if (blogSection) {
      const rect = blogSection.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const targetPosition = rect.top + scrollTop - 20; // 20px offset from top
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  }
  
  updatePaginationButtons() {
    const totalPages = Math.ceil(this.allArticles.length / this.perPage);
    const paginationContainer = document.querySelector('.mt-womo-blog__pagination');
    
    // Hide entire pagination if only one page or no articles
    if (paginationContainer) {
      if (totalPages <= 1) {
        paginationContainer.style.display = 'none';
        return;
      } else {
        paginationContainer.style.display = 'flex';
      }
    }
    
    // Update active state for all numbered buttons (both <a> and <span>)
    const allBtns = document.querySelectorAll('.mt-womo-blog__pagination-btn:not(.mt-womo-blog__pagination-prev):not(.mt-womo-blog__pagination-next)');
    allBtns.forEach(btn => {
      const text = btn.textContent.trim();
      const pageNum = parseInt(text);
      
      if (!isNaN(pageNum)) {
        // Hide buttons for pages that don't exist in filtered results
        if (pageNum > totalPages) {
          btn.style.display = 'none';
        } else {
          btn.style.display = 'inline-flex';
          
          if (pageNum === this.currentPage) {
            btn.classList.add('mt-womo-blog__pagination-btn--active');
          } else {
            btn.classList.remove('mt-womo-blog__pagination-btn--active');
          }
        }
      }
    });
    
    // Also hide dots that are after the last valid page
    const dots = document.querySelectorAll('.mt-womo-blog__pagination-dots');
    dots.forEach(dot => {
      // Get the next sibling to check if we should hide this dot
      const nextBtn = dot.nextElementSibling;
      if (nextBtn) {
        const pageNum = parseInt(nextBtn.textContent.trim());
        if (!isNaN(pageNum) && pageNum > totalPages) {
          dot.style.display = 'none';
        } else {
          dot.style.display = 'inline-block';
        }
      }
    });
    
    // Update prev/next button visibility and href
    const prevBtn = document.querySelector('.mt-womo-blog__pagination-prev');
    const nextBtn = document.querySelector('.mt-womo-blog__pagination-next');
    
    if (prevBtn) {
      if (this.currentPage > 1) {
        prevBtn.style.display = 'inline-flex';
        const prevPage = this.currentPage - 1;
        prevBtn.setAttribute('href', prevPage === 1 ? window.location.pathname : `${window.location.pathname}?page=${prevPage}`);
      } else {
        prevBtn.style.display = 'none';
      }
    }
    
    if (nextBtn) {
      if (this.currentPage < totalPages) {
        nextBtn.style.display = 'inline-flex';
        nextBtn.setAttribute('href', `${window.location.pathname}?page=${this.currentPage + 1}`);
      } else {
        nextBtn.style.display = 'none';
      }
    }
  }

  setupFilterSync() {
    // Sync desktop and mobile filters
    const desktopRadios = document.querySelectorAll('.mt-womo-blog__category-radio');
    const mobileRadios = document.querySelectorAll('.mt-womo-blog__filter-radio');

    desktopRadios.forEach((radio, index) => {
      radio.addEventListener('change', () => {
        if (mobileRadios[index]) {
          mobileRadios[index].checked = radio.checked;
        }
        this.filterPosts(radio.value);
      });
    });

    mobileRadios.forEach((radio, index) => {
      radio.addEventListener('change', () => {
        if (desktopRadios[index]) {
          desktopRadios[index].checked = radio.checked;
        }
        this.filterPosts(radio.value);
        // Close drawer after selection on mobile
        if (window.innerWidth <= 767) {
          this.closeDrawer();
        }
      });
    });
  }

  setupSearch() {
    const searchInputs = document.querySelectorAll('.mt-womo-blog__search-input');
    
    searchInputs.forEach(input => {
      let searchTimeout;
      
      input.addEventListener('input', (e) => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          this.searchPosts(e.target.value);
        }, 300);
      });
    });
  }

  filterPosts(category) {
    const cards = document.querySelectorAll('.mt-womo-blog__card');
    
    // First, collect visible articles based on filter
    this.allArticles = [];
    
    cards.forEach(card => {
      let shouldShow = false;
      
      if (category === 'all') {
        shouldShow = true;
      } else {
        const cardCategories = card.getAttribute('data-category') || '';
        shouldShow = cardCategories.toLowerCase().includes(category.toLowerCase());
      }
      
      if (shouldShow) {
        this.allArticles.push(card);
      }
    });

    // Reset to page 1 when filtering
    this.currentPage = 1;
    
    // Update URL to remove page parameter
    window.history.pushState({ page: 1 }, '', window.location.pathname);
    
    // Apply pagination to filtered results
    this.showCurrentPage();
  }

  searchPosts(query) {
    const cards = document.querySelectorAll('.mt-womo-blog__card');
    const searchTerm = query.toLowerCase().trim();
    
    // First, collect visible articles based on search
    this.allArticles = [];
    
    cards.forEach(card => {
      let shouldShow = false;
      
      if (!searchTerm) {
        shouldShow = true;
      } else {
        const title = card.querySelector('.mt-womo-blog__card-title')?.textContent.toLowerCase() || '';
        const excerpt = card.querySelector('.mt-womo-blog__card-excerpt')?.textContent.toLowerCase() || '';
        const category = card.querySelector('.mt-womo-blog__card-category')?.textContent.toLowerCase() || '';
        
        shouldShow = title.includes(searchTerm) || 
                     excerpt.includes(searchTerm) || 
                     category.includes(searchTerm);
      }
      
      if (shouldShow) {
        this.allArticles.push(card);
      }
    });

    // Reset to page 1 when searching
    this.currentPage = 1;
    
    // Update URL to remove page parameter
    window.history.pushState({ page: 1 }, '', window.location.pathname);
    
    // Apply pagination to search results
    this.showCurrentPage();
  }

  updateYearSections() {
    const yearSections = document.querySelectorAll('.mt-womo-blog__year-section');
    
    yearSections.forEach(section => {
      const visibleCards = section.querySelectorAll('.mt-womo-blog__card[style*="display: block"], .mt-womo-blog__card:not([style*="display: none"])');
      section.style.display = visibleCards.length > 0 ? 'block' : 'none';
    });
  }
  
  handlePageLoad() {
    // Check if we should scroll to blog section
    const shouldScroll = sessionStorage.getItem('scrollToBlog');
    const urlParams = new URLSearchParams(window.location.search);
    const hasPageParam = urlParams.has('page');
    
    if (shouldScroll === 'true' || hasPageParam) {
      // Clear the flag
      sessionStorage.removeItem('scrollToBlog');
      
      // Scroll to blog section
      setTimeout(() => {
        this.scrollToBlogSection();
      }, 100);
    }
  }

  updateStickyOffsets() {
    const header = document.querySelector('.mt-womo-blog__header');
    if (!header) return;

    // Calculate offset from top of viewport considering any sticky site header above
    const sectionEl = document.querySelector('.mt-womo-blog');
    const sectionRect = sectionEl ? sectionEl.getBoundingClientRect() : { top: 0 };

    // Detect theme sticky header height
    const themeHeader = document.querySelector('sticky-header, .shopify-section-header-sticky, header.header--has-sticky, .site-header--sticky, [data-sticky-header]');
    const themeHeaderHeight = themeHeader ? themeHeader.offsetHeight : 0;

    // Search bar sticks at the very top (below theme header)
    const blogSearchTop = themeHeaderHeight;
    document.documentElement.style.setProperty('--blog-search-top', blogSearchTop + 'px');

    // Detect search bar height so STORIES header sticks below it
    const searchEl = document.querySelector('.mt-womo-blog__search--main');
    const searchHeight = searchEl ? searchEl.offsetHeight : 0;

    const blogHeaderTop = blogSearchTop + searchHeight;
    document.documentElement.style.setProperty('--blog-header-top', blogHeaderTop + 'px');

    const headerHeight = header.offsetHeight;
    document.documentElement.style.setProperty('--blog-year-top', (blogHeaderTop + headerHeight) + 'px');
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MTWOMOBlog();
  });
} else {
  new MTWOMOBlog();
}
